import { styled } from 'styled-system/jsx'

const Section = styled('section', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '20px 22px',
    boxShadow: '0 12px 32px -22px rgba(128, 84, 255, 0.18)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
})

const Header = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
})

const HeaderTopRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
})

const Title = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '17px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.2px',
  },
})

const PercentLabel = styled('span', {
  base: {
    fontFamily: 'display',
    fontSize: '14px',
    fontWeight: '700',
    color: 'quickpass.primary',
  },
})

const ProgressTrack = styled('div', {
  base: {
    width: '100%',
    height: '8px',
    backgroundColor: '#F0EDFA',
    borderRadius: 'pill',
    overflow: 'hidden',
  },
})

const ProgressFill = styled('span', {
  base: {
    display: 'block',
    height: '100%',
    background:
      'linear-gradient(90deg, {colors.quickpass.primary} 0%, {colors.quickpass.success} 100%)',
    borderRadius: 'pill',
    transition: 'width {durations.standard} {easings.appleStandard}',
  },
})

const Subtitle = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    margin: '0',
  },
})

const CategoryList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
})

const CategoryBlock = styled('details', {
  base: {
    border: '1.5px solid',
    borderColor: '#F0EDFA',
    borderRadius: 'md',
    overflow: 'hidden',
    transition: 'border-color {durations.fast} {easings.appleStandard}',
    _hover: {
      borderColor: 'quickpass.primarySoft',
    },
  },
})

const CategorySummary = styled('summary', {
  base: {
    listStyle: 'none',
    cursor: 'pointer',
    paddingX: '14px',
    paddingY: '12px',
    display: 'grid',
    gridTemplateColumns: '36px 1fr auto',
    gap: '12px',
    alignItems: 'center',
    backgroundColor: 'quickpass.surfaceCool',
    transition: 'background-color {durations.fast} {easings.appleStandard}',
    _hover: {
      backgroundColor: '#F0EAFF',
    },
  },
})

const CategoryGlyph = styled('span', {
  base: {
    width: '36px',
    height: '36px',
    borderRadius: 'pill',
    backgroundColor: '#FFFFFF',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: '0',
  },
})

const CategoryBody = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
})

const CategoryTitle = styled('span', {
  base: {
    fontFamily: 'display',
    fontSize: '14px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const CategoryHint = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
  },
})

const CategoryProgress = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '700',
    color: 'quickpass.inkSoft',
    backgroundColor: '#FFFFFF',
    paddingX: '10px',
    paddingY: '4px',
    borderRadius: 'pill',
    border: '1px solid',
    borderColor: '#E5E0F5',
  },
  variants: {
    isComplete: {
      true: {
        backgroundColor: 'quickpass.success',
        color: '#FFFFFF',
        borderColor: 'quickpass.success',
      },
    },
  },
})

const TaskList = styled('ul', {
  base: {
    listStyle: 'none',
    padding: '6px 14px 14px 14px',
    margin: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
})

const TaskRow = styled('li', {
  base: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr',
    gap: '12px',
    paddingY: '8px',
    borderBottom: '1px solid',
    borderColor: '#F8F6FF',
    cursor: 'pointer',
    _last: {
      borderBottom: 'none',
    },
  },
})

const TaskCheckbox = styled('span', {
  base: {
    width: '20px',
    height: '20px',
    marginTop: '2px',
    borderRadius: 'sm',
    border: '2px solid',
    borderColor: '#D8D2EC',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: '#FFFFFF',
    backgroundColor: '#FFFFFF',
    transition:
      'background-color {durations.fast} {easings.appleStandard}, border-color {durations.fast} {easings.appleStandard}',
  },
  variants: {
    isChecked: {
      true: {
        backgroundColor: 'quickpass.success',
        borderColor: 'quickpass.success',
      },
    },
  },
})

const TaskBody = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: '0',
  },
})

const TaskTitle = styled('span', {
  base: {
    fontFamily: 'display',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.ink',
  },
  variants: {
    isChecked: {
      true: {
        textDecoration: 'line-through',
        color: 'quickpass.inkSoft',
      },
    },
  },
})

const TaskHint = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
    lineHeight: '1.4',
  },
})

export {
  Section,
  Header,
  HeaderTopRow,
  Title,
  PercentLabel,
  ProgressTrack,
  ProgressFill,
  Subtitle,
  CategoryList,
  CategoryBlock,
  CategorySummary,
  CategoryGlyph,
  CategoryBody,
  CategoryTitle,
  CategoryHint,
  CategoryProgress,
  TaskList,
  TaskRow,
  TaskCheckbox,
  TaskBody,
  TaskTitle,
  TaskHint,
}
